import { redirect } from '@tanstack/react-router'

import { isSuccess } from '@orus.eu/result'
import { getMissingPermissions, type AnyOfPermission, type Permission } from '@orus.eu/right-access-management'

import { buildLoginHref } from './build-login-href.ts'
import { recordLoginBounce, resetLoginBounce } from './detect-login-redirect-loop.ts'
import type { SessionAccess } from './session-access.ts'

/**
 * Page-level access guard, called inside a route `beforeLoad`. Reads the session out of the router context,
 * and throws a redirect to the login page when the user is anonymous or lacks a required permission.
 *
 * Note: logged-in-but-forbidden currently redirects to login. A 403-in-place via errorComponent is a deliberate
 * follow-up (cf. routing-frontend.md, Décision 8).
 */
export async function requirePermissions(
  context: { session: SessionAccess },
  requiredPermissions: (Permission | AnyOfPermission)[],
): Promise<void> {
  const sessionResult = await context.session.getSessionResult()

  // Session unavailable (client outdated): handled elsewhere by SessionManager — do not block the route.
  if (!isSuccess(sessionResult)) return

  const session = sessionResult.output

  if (!session.user) {
    const loginHref = buildLoginHref()
    recordLoginBounce(loginHref)
    throw redirect({ href: loginHref })
  }

  const missingPermissions = getMissingPermissions(session.allPermissions, requiredPermissions)
  if (missingPermissions.length > 0) {
    const loginHref = buildLoginHref()
    recordLoginBounce(loginHref)
    throw redirect({ href: loginHref })
  }

  // Reached an authorized page: any in-flight bounce episode ended without looping
  resetLoginBounce()
}
